import React from 'react';
import { LightBulbIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage1 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 1: Align on Purpose of Performance" icon={<LightBulbIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Get crystal clear alignment across leadership on why you need to measure performance</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Create a short 2-3 sentence performance philosophy that defines how performance will be used</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Document what you can't do without this framework to build business case</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Why Do We Need This? Building Your Performance Philosophy</h3>
        <p className="text-gray-300 mb-6">
          Before building anything, you need crystal clear alignment on why performance measurement matters in your organization. 
          Different stakeholders often have different expectations, which can derail your framework if not addressed upfront.
        </p>

        <div className="bg-gray-800/50 p-6 rounded-lg border-l-4 border-primary-light mb-6">
          <h4 className="text-white font-semibold mb-3">Common (Conflicting) Stakeholder Views:</h4>
          <ul className="space-y-3">
            <li className="flex items-start">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-3 mt-1 flex-shrink-0" />
              <div>
                <span className="text-white font-medium">Sales:</span>
                <span className="text-gray-300"> "We don't need one. Our numbers do the talking for us"</span>
              </div>
            </li>
            <li className="flex items-start">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-3 mt-1 flex-shrink-0" />
              <div>
                <span className="text-white font-medium">Executive:</span>
                <span className="text-gray-300"> "We need a way to remove low performers"</span>
              </div>
            </li>
            <li className="flex items-start">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-3 mt-1 flex-shrink-0" />
              <div>
                <span className="text-white font-medium">Finance:</span>
                <span className="text-gray-300"> "We need to work out how we reward top performers"</span>
              </div>
            </li>
            <li className="flex items-start">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-3 mt-1 flex-shrink-0" />
              <div>
                <span className="text-white font-medium">Employees:</span>
                <span className="text-gray-300"> "We want feedback on how we can grow and develop"</span>
              </div>
            </li>
          </ul>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">What a Good Performance Framework Should Do:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Help folks understand what 'excellence' looks like in your context</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Baseline and reinforce expectations across the organization</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Act as an accountability, growth and development aid</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Help you manage and recover from underperformance</span>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-3">Key Questions Your Framework Should Answer:</h4>
        <ol className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">1.</span>
            <span className="text-gray-300">Are we as a business getting ROI on our biggest cost item (our people)?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">2.</span>
            <span className="text-gray-300">Are people impacting at the level we need them to hit our goals?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">3.</span>
            <span className="text-gray-300">What's the difference between 'good' and 'great' here?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">4.</span>
            <span className="text-gray-300">Who are our best performers, and lowest performers right now?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">5.</span>
            <span className="text-gray-300">What's expected of me as an employee in my role right now?</span>
          </li>
        </ol>

        <ExpandableCallout type="tip" title="Alternative Framing: What CAN'T We Do Without This?" className="mb-6">
          <p className="text-gray-300 mb-4">Another way to frame this is asking: "What can't we do if we don't have this framework?"</p>
          <p className="text-gray-300 mb-3">Perhaps you can't:</p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Retain, reward, promote the right people</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Exit the right people when needed</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Identify who needs support to improve</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Set expectations and hold each other accountable</span>
            </li>
          </ul>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Creating Your Performance Philosophy</h3>
        <p className="text-gray-300 mb-6">
          If you can't answer the "why" question confidently yet, take this back to your Senior Leadership Team 
          to align using the points and questions above.
        </p>

        <CopyToNotion title="Performance Philosophy Template">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">Our Performance Philosophy</h4>
              <p className="text-gray-300 text-sm mb-2">Complete these sentences:</p>
              <div className="bg-gray-800 p-4 rounded border border-gray-700">
                <p className="text-gray-300 mb-2">Performance matters at [Company Name] because _______.</p>
                <p className="text-gray-300 mb-2">We use performance assessments to _______.</p>
                <p className="text-gray-300">Our goal is to help people _______ while ensuring the business _______.</p>
              </div>
            </div>
          </div>
        </CopyToNotion>

        <div className="mt-8 p-4 bg-gray-800/30 border border-gray-700 rounded-lg">
          <div className="flex items-center mb-4">
            <span className="bg-primary/20 text-primary-light px-3 py-1 rounded-full text-sm font-semibold mr-3">
              Example
            </span>
            <h4 className="text-white font-semibold">How Gorilla Defined Their Performance Philosophy</h4>
          </div>
          <p className="text-gray-300 text-sm mb-4">
            Here's how one company articulated their "why" for performance management:
          </p>
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/performance/gorilla-performance-philosophy.png"
            alt="Example of Gorilla's Performance Handbook showing their performance philosophy"
            className="rounded-lg border border-gray-700"
          />
          <p className="text-gray-400 text-xs text-center mt-3">
            Gorilla's Performance Handbook - Notice how they clearly state their purpose and approach
          </p>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">A short 2-3 sentence performance philosophy that defines why performance matters and how it's used</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Leadership alignment on the purpose and approach to performance measurement</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Clear documentation of what you can't achieve without this framework</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Next:</strong> Move to <Link to="/playbooks/how-to-build-launch-embed-performance/stage-2" className="text-primary hover:text-primary-light">Stage 2: Assess Your Building Blocks</Link> 
              to evaluate the foundations needed for your framework.
            </p>
          </div>
        </div>
      </PlaybookCard>

      <ExpandableCallout type="tip" title="How Do You Know This Stage Is Complete?" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">You're ready to move to Stage 2 when you have:</p>
          
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="bg-green-500/20 rounded-full p-1 mr-3 mt-1">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div>
                <h5 className="text-white font-medium">Written Performance Philosophy</h5>
                <p className="text-gray-300 text-sm">A clear 2-3 sentence statement that defines why performance matters and how it will be used</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-green-500/20 rounded-full p-1 mr-3 mt-1">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div>
                <h5 className="text-white font-medium">Leadership Alignment</h5>
                <p className="text-gray-300 text-sm">Senior leadership team agreement on the purpose and approach</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-green-500/20 rounded-full p-1 mr-3 mt-1">
                <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
              </div>
              <div>
                <h5 className="text-white font-medium">Business Case Documentation</h5>
                <p className="text-gray-300 text-sm">Clear articulation of what you can't do without this framework</p>
              </div>
            </div>
          </div>
          
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mt-4">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Success Metric:</strong> You can explain the framework's purpose in 30 seconds and get nods of agreement from any leadership team member.
            </p>
          </div>
        </div>
      </ExpandableCallout>

      <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">
            Some stakeholders may argue this philosophical work slows down "building the thing." However, 
            without clear purpose and alignment, your framework will likely fail to gain adoption or 
            deliver meaningful results.
          </p>
          <div className="bg-gray-800/50 p-4 rounded-lg border-l-4 border-primary-light">
            <p className="text-gray-300">
              <strong className="text-white">Pro tip:</strong> When facing resistance, bring the conversation back to business impact. 
              Ask: "How will we know if our people investment is working without clear performance indicators?"
            </p>
          </div>
        </div>
      </ExpandableCallout>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage1; 